import type * as THREE from 'three';

export type Quality = 'low' | 'medium' | 'high';

/** What each graphics preset turns on. Zones read the active profile every frame. */
export interface QualityProfile {
  /** Upper bound for the canvas pixel ratio (the dynamic-resolution governor may go lower). */
  pixelRatioCap: number;
  /** Lowest pixel ratio the governor may fall back to when frames run long. */
  pixelRatioMin: number;
  shadows: boolean;
  shadowMapSize: number;
  /** Soft-shadow filter radius (PCF). */
  shadowRadius: number;
  /** Shadow box half-size around the player (m). */
  shadowHalf: number;
  bloom: boolean;
  /** Bloom mip-chain resolution scale. */
  bloomScale: number;
  smaa: boolean;
  /** Real point lights pooled over the nearest light/fire sockets (constant count: no shader recompiles). */
  pointLights: number;
  /** VFX particle multiplier. */
  particleDensity: number;
  /** Ambient motes (pollen, embers, snow…) around the camera. */
  ambientParticles: number;
  /** Image-based lighting (metals, fill). */
  envMap: boolean;
  /** Which monsters cast real shadows (the rest keep their blob shadow). */
  creatureShadows: 'all' | 'large' | 'none';
  /** Props lower than this (m) do not cast shadows. */
  propShadowMinHeight: number;
  /** Beyond this distance (m) from the camera focus, animation mixers tick at a reduced rate. */
  animNear: number;
  /** Red battle outline on bosses and blue counter outline. */
  outlines: boolean;
}

export const QUALITY: Record<Quality, QualityProfile> = {
  low: {
    pixelRatioCap: 1,
    pixelRatioMin: 0.75,
    shadows: false,
    shadowMapSize: 1024,
    shadowRadius: 1,
    shadowHalf: 22,
    bloom: true,
    bloomScale: 0.5,
    smaa: false,
    pointLights: 0,
    particleDensity: 0.5,
    ambientParticles: 0,
    envMap: false,
    creatureShadows: 'none',
    propShadowMinHeight: 99,
    animNear: 18,
    outlines: true,
  },
  medium: {
    pixelRatioCap: 1.25,
    pixelRatioMin: 1,
    shadows: true,
    shadowMapSize: 1024,
    shadowRadius: 2,
    shadowHalf: 24,
    bloom: true,
    bloomScale: 0.5,
    smaa: true,
    pointLights: 2,
    particleDensity: 0.75,
    ambientParticles: 140,
    envMap: true,
    creatureShadows: 'large',
    propShadowMinHeight: 1.2,
    animNear: 28,
    outlines: true,
  },
  high: {
    pixelRatioCap: 2,
    pixelRatioMin: 1,
    shadows: true,
    shadowMapSize: 2048,
    shadowRadius: 3,
    shadowHalf: 28,
    bloom: true,
    bloomScale: 1,
    smaa: true,
    pointLights: 4,
    particleDensity: 1,
    ambientParticles: 320,
    envMap: true,
    creatureShadows: 'all',
    propShadowMinHeight: 0.5,
    animNear: 45,
    outlines: true,
  },
};

/**
 * Presentation state shared by the renderer and the zone views: the active
 * quality profile and the gameplay camera (for culling and camera-relative
 * effects). Set by `GameRenderer`; read by `ZoneRuntime` and the VFX.
 */
export const renderCtx = {
  quality: 'high' as Quality,
  profile: QUALITY.high,
  camera: null as THREE.PerspectiveCamera | null,
  /** Bumped whenever the quality changes (zones re-apply quality-dependent settings). */
  version: 0,
};

export function setRenderQuality(q: Quality): void {
  if (renderCtx.quality === q && renderCtx.version > 0) return;
  renderCtx.quality = q;
  renderCtx.profile = QUALITY[q];
  renderCtx.version++;
}
